import { pick } from 'lodash';
import { DateTime } from 'luxon';
import { nanoid } from 'nanoid';

import {
  useCurrentUser,
  usePcfraPerson,
  useProperty,
  useReferenceListSelectOptions,
  useUpdatePcfraStatus,
  useUserStore,
} from '~/client/hooks/storeHooks';
import { useGoBackOrNavigate } from '~/client/hooks/useGoBackOrNavigate';
import { api } from '~/client/lib/trpc';
import { fileFromBase64String, openObjectInNewWindow } from '~/client/utils/file';
import { toPcrfaEditPage, toPcrfaPropertyPage } from '~/client/utils/navigation';
import { PcfraDraft, PcfraDraftValues, PcfraValues } from '~/common/types/pcfra';
import { PcfraStatus } from '~/common/types/people';

import { formValuesToUpdatePayload as contactDetailsFormValuesToUpdatePayload } from '../PersonPage/ContactDetailsEditPage/helpers';
import { hasValidContactDetails, hasValidReferrer } from './helpers';
import { PcfraSection } from './types';

type Options = {
  propertyId: number;
  personId: number;
  existingDraft?: PcfraDraft;
  updatedDraftValues: PcfraDraftValues;
  hasChanges: boolean;
  canApprove?: boolean;
  currentSection?: PcfraSection;
};

export const usePcfraActions = ({
  propertyId,
  personId,
  existingDraft,
  updatedDraftValues,
  hasChanges,
  canApprove,
  currentSection,
}: Options) => {
  const navigate = useGoBackOrNavigate();

  const user = useCurrentUser();

  const userStore = useUserStore();
  const { propertyRef = '' } = useProperty(propertyId) || {};
  const person = usePcfraPerson(personId);
  const updatePcfraStatus = useUpdatePcfraStatus();
  const referenceListSelectOptions = useReferenceListSelectOptions();

  const { mutateAsync: submitPcfraForApproval } = api.submitPcfraForApproval.useMutation();
  const { mutateAsync: approvePcfra, isPending: isApproving } = api.approvePcfra.useMutation();

  const saveFloorLevel = () => {
    const updatedFloorLevel = updatedDraftValues.details?.floorLevel;
    const shouldSaveFloorLevel =
      currentSection === 'details' &&
      person &&
      updatedFloorLevel &&
      referenceListSelectOptions('FLOORLEVEL').some(({ value }) => value === updatedFloorLevel);

    if (shouldSaveFloorLevel) {
      userStore?.handleAndDispatchMessage('updatePropertyAttributes', propertyId, {
        propertyId,
        propertyRef,
        tenancyId: person.tenancyId,
        isPcfra: true,
        floorLevel: updatedFloorLevel,
      });
    }
  };

  const saveContactDetails = () => {
    const updatedContactDetails = updatedDraftValues.details?.contactDetails;
    const shouldSaveDetails =
      currentSection === 'details' &&
      person &&
      updatedContactDetails &&
      hasValidContactDetails(updatedContactDetails);

    if (shouldSaveDetails) {
      const payload = contactDetailsFormValuesToUpdatePayload(person, updatedContactDetails);
      userStore?.handleAndDispatchMessage('updateContactDetails', personId, payload);
    }
  };

  const saveReferrer = () => {
    const existingReferrer =
      (existingDraft?.values as PcfraDraftValues)?.details?.referrerEmailAddress ?? '';
    const updatedReferrer = updatedDraftValues.details?.referrerEmailAddress ?? '';

    const requiresAction =
      currentSection === 'details' && person && existingReferrer !== updatedReferrer;

    const shouldSaveReferrer = requiresAction && hasValidReferrer(updatedReferrer);
    const shouldRemoveReferrer = requiresAction && !updatedReferrer;

    if (shouldSaveReferrer) {
      userStore?.handleAndDispatchMessage('updatePcfraReferrer', personId, {
        propertyId,
        personId,
        emailAddress: updatedReferrer,
      });
    } else if (shouldRemoveReferrer) {
      userStore?.handleAndDispatchMessage('deletePcfraReferrer', personId, {
        propertyId,
        personId,
      });
    }
  };

  const saveDraft = () => {
    if (!user) {
      throw Error('No user found');
    }
    const now = DateTime.now().toISO();
    const draftId = existingDraft?.draftId || nanoid();

    if (existingDraft && !hasChanges) {
      return draftId;
    }
    saveFloorLevel();
    saveContactDetails();
    saveReferrer();

    const currentAuthor = pick(user, ['id', 'name', 'email', 'camdenAccountName']);

    userStore?.handleAndDispatchMessage('savePcfraDraft', draftId, {
      draftId,
      propertyId,
      personId,
      values: updatedDraftValues,
      createdBy: existingDraft?.createdBy || currentAuthor,
      modifiedBy: currentAuthor,
      created: existingDraft?.created || now,
      modified: now,
    });

    if (person?.pcfraStatus !== PcfraStatus.Drafting) {
      const newStatus: PcfraStatus = PcfraStatus.Drafting;
      updatePcfraStatus({ propertyId, personId, newStatus });
    }
    // Update URL if new draft has been created
    if (!existingDraft) {
      void navigate(toPcrfaEditPage(propertyId, personId, draftId), { replace: true });
    }
    return draftId;
  };

  const getPcfraUrl = (draftId: string) =>
    `${window.location.origin}${toPcrfaEditPage(propertyId, personId, draftId)}`;

  const goToPcfraPropertyPage = () => navigate(toPcrfaPropertyPage(propertyId));

  const submitForApproval = async () => {
    const draftId = saveDraft();
    const { success } = await submitPcfraForApproval({ pcfraUrl: getPcfraUrl(draftId) });

    if (success) {
      updatePcfraStatus({ propertyId, personId, newStatus: PcfraStatus.Submitted });
      void goToPcfraPropertyPage();
    }
  };

  const approve = async () => {
    if (!canApprove || !user) {
      return;
    }
    saveDraft();

    const pcfraValues = updatedDraftValues as PcfraValues;

    const { doc, filename } = await approvePcfra({
      personId,
      pcfraValues,
    });

    userStore?.store.transaction(() => {
      updatePcfraStatus({
        propertyId,
        personId,
        newStatus: PcfraStatus.Active,
        shouldNotifyReferrer: true,
      });

      userStore?.handleAndDispatchMessage('updatePcfraReviewDate', personId, {
        propertyId,
        personId,
        newDate: pcfraValues.actionsRecommendations.reviewDate,
      });
    });

    const docFile = await fileFromBase64String(doc, filename, 'application/pdf');
    openObjectInNewWindow(docFile);

    void goToPcfraPropertyPage();
  };

  return { saveDraft, submitForApproval, approve, goToPcfraPropertyPage, isApproving };
};
